import {
	Box,
	Button,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormHelperText,
	Grid,
	OutlinedInput,
	Stack,
	Typography,
	useTheme,
} from '@mui/material';
import { FC, useEffect } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { z } from 'zod';
import { FormField } from '../../../../form/FormField';
import { useTypedForm } from '../../../../../../shared/fe/hooks/useForm';
import { useModalContext } from '../../../../modal/ModalContext';
import { useApiMutation } from '../../../../query/hooks';
import { mutationResponseHandler } from '../../../../utils/mutationResponseHandler';
import { graphql } from '../../../../gql';
import { getNightOrDayPalette } from '../../../../utils/getNightOrDayPalette';
import { useSnackbar } from '../../../../store/snackbar';

const organizationSettings_updateOrganization = graphql(`
	mutation organizationSettings_updateOrganization($name: String!, $alias: String!) {
		updateOrganization(config: { name: $name, alias: $alias }) {
			ok
			errorMessage
		}
	}
`);

const editOrganizationModalSchema = z.object({
	name: z.string().optional(),
	url: z.string().optional(),
});

const createFormSchema = (intl: IntlShape) =>
	z.object({
		name: z.string().nonempty(intl.formatMessage({ id: 'form.organization.settings.create.error.empty-name' })),
		url: z.string().nonempty(intl.formatMessage({ id: 'form.organization.settings.create.error.empty-url' })),
	});

type Props = z.infer<typeof editOrganizationModalSchema>;

type FormValues = z.TypeOf<ReturnType<typeof createFormSchema>>;

const OrganizationEdit: FC<Props> = ({ name, url }) => {
	const intl = useIntl();
	const theme = useTheme();
	const { closeModal } = useModalContext();
	const { openSnackbar } = useSnackbar();

	const form = useTypedForm({
		schema: createFormSchema(intl),
	});

	const { formState, reset, handleSubmit } = form;

	const { mutate: updateOrganization } = useApiMutation(organizationSettings_updateOrganization, {
		onSuccess: async (result) => {
			mutationResponseHandler({
				result: result.updateOrganization.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'form.organization.settings.edit.success' }),
					error: intl.formatMessage({ id: 'common.error.default' }),
				},
				errorMessage: result.updateOrganization.errorMessage,
				openSnackbar,
			});
		},
	});

	useEffect(() => {
		reset({
			name: name ?? '',
			url: url ?? '',
		});
	}, [reset, name, url]);

	const onSubmit = (data: FormValues) => {
		updateOrganization(
			{
				name: data.name,
				alias: data.url,
			},
			{
				onSuccess: () => {
					closeModal();

					if (data.url !== url) {
						const urlObj = new URL(window.location.href);
						const domainParts = urlObj.hostname.split('.');
						if (domainParts.length > 2 || domainParts[1] === 'localhost') {
							domainParts[0] = data.url; // assumes the first part is the subdomain
						} else {
							domainParts.unshift(data.url); // Add the subdomain if it wasn't there
						}
						urlObj.hostname = domainParts.join('.');
						window.location.assign(urlObj.href);
					}
				},
			},
		);
	};

	return (
		<form noValidate onSubmit={handleSubmit(onSubmit)}>
			<DialogTitle>
				<FormattedMessage id={'form.organization.settings.edit.title'} />
			</DialogTitle>
			<Divider />
			<DialogContent>
				<Grid container spacing={3}>
					<Grid item xs={12}>
						<Stack gap={3}>
							<FormField
								form={form}
								name={'name'}
								label={intl.formatMessage({ id: 'form.organization.settings.edit.name' })}
								render={(props) => <OutlinedInput {...props} />}
							/>
							<FormField
								form={form}
								name={'url'}
								label={intl.formatMessage({ id: 'form.organization.settings.edit.url' })}
								render={(props) => (
									<OutlinedInput
										{...form.register('url')}
										{...props}
										endAdornment={
											<Box
												display={'flex'}
												paddingX={1.5}
												position={'absolute'}
												right={0}
												height={'100%'}
												alignItems={'center'}
												border={`1px solid ${theme.palette[getNightOrDayPalette(theme)][300]}`}
												borderRadius={'0 4px 4px 0'}
												sx={{ backgroundColor: theme.palette[getNightOrDayPalette(theme)][50] }}
											>
												<Typography color={theme.palette[getNightOrDayPalette(theme)][500]}>
													<FormattedMessage
														id={'form.organization.settings.edit.url-adornment'}
														values={{ environment: import.meta.env.NODE_ENV === 'production' ? 'app' : 'dev' }}
													/>
												</Typography>
											</Box>
										}
									/>
								)}
							/>
						</Stack>
					</Grid>

					{formState.errors.root != null && (
						<Grid item xs={12}>
							<FormHelperText error>{formState.errors.root.message}</FormHelperText>
						</Grid>
					)}
				</Grid>
			</DialogContent>
			<Divider />
			<DialogActions sx={{ p: 2.5 }}>
				<Stack direction={'row'} spacing={2} alignItems={'center'} width={'100%'} justifyContent={'end'}>
					<Button color={'secondary'} onClick={closeModal}>
						<FormattedMessage id={'common.cancel'} />
					</Button>
					<Button type={'submit'} variant={'contained'} disabled={formState.isSubmitting}>
						<FormattedMessage id={'common.save'} />
					</Button>
				</Stack>
			</DialogActions>
		</form>
	);
};

export { OrganizationEdit, editOrganizationModalSchema };
